import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const GLOW_ICON_MAP = {
  "stat-glow-green": "stat-icon-green",
  "stat-glow-amber": "stat-icon-amber",
  "stat-glow-red": "stat-icon-purple",
  "stat-glow-indigo": "stat-icon-indigo",
  "stat-glow-sky": "stat-icon-sky",
  "stat-glow-blue": "stat-icon-sky",
};

const formatPercentage = (pct) => {
  const num = Number(pct);
  if (Number.isNaN(num)) return "0%";
  return Number.isInteger(num) ? `${num}%` : `${num.toFixed(1)}%`;
};

const formatValue = ({ value, percentage, suffix = "", loading }) => {
  if (loading) return "—";
  if (value === null || value === undefined || value === "") return "—";

  if (percentage !== null && percentage !== undefined && percentage !== "") {
    return (
      <>
        {value}
        <span className="text-lg font-normal text-muted-foreground">
          {" "}({formatPercentage(percentage)})
        </span>
      </>
    );
  }

  return `${value}${suffix}`;
};

const StatCard = ({
  title,
  value,
  percentage,
  icon: Icon,
  glowClass,
  delay = "",
  loading = false,
  suffix = "",
  items,
  onClick,
  onItemClick,
  active = false,
  className,
  itemsClassName,
  testIdPrefix = "stat",
}) => {
  const iconClass = GLOW_ICON_MAP[glowClass] ?? "stat-icon-sky";
  const isGrouped = Array.isArray(items) && items.length > 0;
  const isClickable = typeof onClick === "function";
  const testId = `${testIdPrefix}-${(title ?? items?.[0]?.title ?? "card")
    .toLowerCase()
    .replace(/\s/g, "-")}`;

  const handleCardKeyDown = (e) => {
    if (!isClickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      className={cn(
        "card-hover animate-fade-in min-w-0",
        delay,
        glowClass,
        isClickable && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group/card",
        className,
      )}
      data-testid={testId}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-pressed={isClickable ? active : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleCardKeyDown : undefined}
    >
      <CardContent className="pt-5 pb-4 px-5">
        {isGrouped ? (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {title && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2.5">
                  {title}
                </p>
              )}
              <div
                className={cn(
                  "grid gap-2",
                  itemsClassName
                    ?? (items.length >= 8
                      ? "grid-cols-2 sm:grid-cols-5"
                      : items.length === 4
                        ? "grid-cols-2"
                        : items.length >= 3
                          ? "grid-cols-3"
                          : items.length > 1
                            ? "grid-cols-2"
                            : "grid-cols-1"),
                )}
              >
                {items.map((item) => {
                  const itemClickable = typeof (item.onClick ?? onItemClick) === "function";
                  const itemActive = Boolean(item.active);
                  const handleItemClick = (e) => {
                    if (!itemClickable) return;
                    e.stopPropagation();
                    (item.onClick ?? onItemClick)?.(item);
                  };
                  const handleItemKeyDown = (e) => {
                    if (!itemClickable) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      (item.onClick ?? onItemClick)?.(item);
                    }
                  };

                  return (
                    <div
                      key={item.key ?? item.title}
                      className={cn(
                        "min-w-0 rounded-md",
                        itemClickable && [
                          "group/item cursor-pointer select-none",
                          "border bg-muted/40 px-1.5 py-1",
                          "transition-colors hover:border-[var(--xl-line2)] hover:bg-muted/80",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          itemActive
                            ? "border-[var(--xl-line2)] bg-muted/80"
                            : "border-transparent",
                        ],
                      )}
                      role={itemClickable ? "button" : undefined}
                      tabIndex={itemClickable ? 0 : undefined}
                      aria-pressed={itemClickable ? itemActive : undefined}
                      onClick={itemClickable ? handleItemClick : undefined}
                      onKeyDown={itemClickable ? handleItemKeyDown : undefined}
                      aria-label={itemClickable ? `View ${item.title}` : undefined}
                      data-testid={item.key ? `${testIdPrefix}-item-${item.key}` : undefined}
                    >
                      <p className="text-[10px] font-medium text-muted-foreground truncate">
                        {item.title}
                      </p>
                      <p
                        className={cn(
                          "font-bold mt-0.5 tracking-tight tabular-nums whitespace-nowrap",
                          items.length >= 3 ? "text-xl" : "text-2xl",
                          itemClickable && "group-hover/item:text-[var(--xl-primary)] transition-colors",
                          itemActive && "text-[var(--xl-primary)]",
                        )}
                      >
                        {formatValue({
                          value: item.value,
                          percentage: item.percentage,
                          suffix: item.suffix,
                          loading,
                        })}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
            {Icon && (
              <div className={cn("w-6 h-6 shrink-0 rounded-md flex items-center justify-center", iconClass)}>
                <Icon className="w-3 h-3" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <div
              className={cn(
                "min-w-0",
                isClickable && [
                  "rounded-md border bg-muted/40 px-2 py-1.5",
                  "transition-colors group-hover/card:border-[var(--xl-line2)] group-hover/card:bg-muted/80",
                  active
                    ? "border-[var(--xl-line2)] bg-muted/80"
                    : "border-transparent",
                ],
              )}
            >
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {title}
              </p>
              <p
                className={cn(
                  "text-3xl font-bold mt-1 tracking-tight tabular-nums",
                  isClickable && "group-hover/card:text-[var(--xl-primary)] transition-colors",
                  active && "text-[var(--xl-primary)]",
                )}
              >
                {formatValue({ value, percentage, suffix, loading })}
              </p>
            </div>
            {Icon && (
              <div className={cn("w-6 h-6 shrink-0 rounded-md flex items-center justify-center", iconClass)}>
                <Icon className="w-3 h-3" />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StatCard;
